import { Button, CircularProgress, Dialog } from "@mui/material";
import React from "react";
import {
  InvoiceDialogCloseButton,
  invoiceDesignFontFamily,
  invoiceDesignTokens,
  invoiceDialogPaperSx,
  invoiceModalActionSx,
} from "./InvoiceDesignSystem";
import ProcessPaymentFacts from "./ProcessPaymentFacts";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import {
  applyCmpOrganizationNoun,
  applyCmpOrganizationNounToPageName,
} from "../../../utils/cmpOrganizationNoun";

export type ProcessPaymentMethodPresentation = Readonly<{
  disabled?: boolean;
  id: string;
  label: string;
}>;

type ProcessPaymentDialogShellProps = Readonly<{
  afterPaymentMethods?: React.ReactNode;
  alerts?: React.ReactNode;
  balanceOwing: string;
  billTo: string;
  busy?: boolean;
  cancelLabel?: string;
  emptyPaymentMethodMessage?: string | null;
  invoiceNumber: string;
  onClose: () => void;
  onConfirm?: () => void;
  onPaymentMethodChange: (methodId: string) => void;
  open: boolean;
  paymentDetails: React.ReactNode;
  paymentMethods: readonly ProcessPaymentMethodPresentation[];
  primaryActionDisabled?: boolean;
  primaryActionLabel?: string;
  selectedPaymentMethodId: string;
}>;

// The shell is an expression component, so the noun hook lives here.
const EmptyPaymentMethodMessage = ({
  message,
}: {
  message?: string | null;
}): React.ReactElement => {
  const noun = useCmpOrganizationNoun();
  return (
    <>
      {message
        ? applyCmpOrganizationNoun(message, noun)
        : `No payment methods are turned on. Go to ${applyCmpOrganizationNoun("My Chamber", noun)} > ${applyCmpOrganizationNounToPageName("Chamber Set Up", noun)} > Global Settings, then turn one on under Payment Options.`}
    </>
  );
};

const ProcessPaymentDialogShell = ({
  afterPaymentMethods,
  alerts,
  balanceOwing,
  billTo,
  busy = false,
  cancelLabel = "Cancel",
  emptyPaymentMethodMessage,
  invoiceNumber,
  onClose,
  onConfirm,
  onPaymentMethodChange,
  open,
  paymentDetails,
  paymentMethods,
  primaryActionDisabled = false,
  primaryActionLabel = "Confirm & Record Payment",
  selectedPaymentMethodId,
}: ProcessPaymentDialogShellProps): React.ReactElement => (
  <Dialog
    disableEscapeKeyDown={busy}
    fullWidth
    maxWidth={false}
    onClose={busy ? undefined : onClose}
    open={open}
    PaperProps={{ sx: invoiceDialogPaperSx({ maxWidth: 700 }) }}
  >
    <div
      className="flex max-h-[92vh] min-h-0 flex-col bg-white"
      style={{ fontFamily: invoiceDesignFontFamily }}
    >
      <div className="relative min-h-0 flex-1 overflow-y-auto px-[18px] pb-4 pt-4 sm:px-7 sm:pb-4 sm:pt-[22px]">
        <InvoiceDialogCloseButton
          ariaLabel="Close process payment"
          disabled={busy}
          onClick={onClose}
        />
        <h2 className="pr-9 text-[18px] font-bold leading-[27px] text-[#0A0A0A] sm:text-[21px]">
          Process Payment
        </h2>

        {alerts ? <div className="mt-3 space-y-2">{alerts}</div> : null}

        <div className="mt-3">
          <ProcessPaymentFacts
            balanceOwing={balanceOwing}
            billTo={billTo}
            invoiceNumber={invoiceNumber}
          />
        </div>

        <div className="mt-4">{paymentDetails}</div>

        <section className="mt-4 rounded-[10px] border border-[#0091FF] p-3 sm:p-4">
          <h3 className="mb-3 text-[13.5px] font-bold uppercase leading-[17px] tracking-[0.06em] text-[#0091FF]">
            Select Payment Method
          </h3>
          {paymentMethods.length ? (
            <div
              className="grid grid-cols-2 gap-2.5 sm:grid-cols-5"
              style={{ gridTemplateRows: "auto auto" }}
            >
              {paymentMethods.map((method) => {
                const selected = method.id === selectedPaymentMethodId;

                return (
                  <div
                    className={`grid min-w-0 content-start gap-y-2 rounded-lg border p-2 text-center ${
                      selected
                        ? "border-[#0091FF]"
                        : "border-transparent hover:border-[#8CCBFA]"
                    }`}
                    key={method.id}
                    style={{
                      gridRow: "span 2",
                      gridTemplateRows: "subgrid",
                    }}
                  >
                    <span className="self-start break-words text-[14px] font-semibold leading-[18px] text-[#0A0A0A]">
                      {method.label}
                    </span>
                    <button
                      aria-pressed={selected}
                      className={`flex min-h-[34px] w-full items-center justify-center self-end rounded-[7px] border border-[#0091FF] px-3 py-[7px] text-[14px] font-semibold leading-[18px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0091FF] disabled:cursor-not-allowed disabled:opacity-50 ${
                        selected
                          ? "bg-[#0091FF] text-white"
                          : "bg-white text-[#0091FF]"
                      }`}
                      disabled={busy || method.disabled}
                      onClick={() => onPaymentMethodChange(method.id)}
                      type="button"
                    >
                      Select
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm leading-5 text-[#4B5563]">
              <EmptyPaymentMethodMessage message={emptyPaymentMethodMessage} />
            </p>
          )}
        </section>

        {afterPaymentMethods ? (
          <div className="mt-3">{afterPaymentMethods}</div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-[#F4F8FF] px-[18px] py-3 sm:px-7 sm:py-4">
        {onConfirm ? (
          <Button
            disabled={busy || primaryActionDisabled}
            fullWidth
            onClick={onConfirm}
            startIcon={
              busy ? <CircularProgress color="inherit" size={16} /> : undefined
            }
            sx={{
              ...invoiceModalActionSx({
                backgroundColor: invoiceDesignTokens.blue,
                hoverBackgroundColor: "#0079D6",
              }),
              flex: "1 1 260px",
            }}
            variant="contained"
          >
            {primaryActionLabel}
          </Button>
        ) : null}
        <Button
          disabled={busy}
          onClick={onClose}
          sx={{
            backgroundColor: "white",
            border: `1px solid ${invoiceDesignTokens.border}`,
            borderRadius: "8px",
            color: invoiceDesignTokens.ink,
            fontFamily: invoiceDesignFontFamily,
            fontSize: 14,
            minHeight: 44,
            minWidth: 95,
            px: 3,
            textTransform: "none",
            width: { xs: "100%", sm: "auto" },
            "&:hover": {
              backgroundColor: "#F9FAFB",
              borderColor: "#B7BCC2",
            },
          }}
          variant="outlined"
        >
          {cancelLabel}
        </Button>
      </div>
    </div>
  </Dialog>
);

export default ProcessPaymentDialogShell;
